import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Marketplace.css';
import {
    ShoppingBag,
    Search,
    Filter,
    MapPin,
    Eye,
    Heart,
    Shield,
    AlertTriangle,
    Plus,
    ChevronDown,
    Package,
    Tag,
    Gift
} from 'lucide-react';
import { clubsService, marketplaceService } from '../../../../services';
import StateCard from '../../../components/feedback/StateCard';
import { getClubPublicPath } from '../../../../utils/clubPublicPaths';

const Marketplace = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [listings, setListings] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(true);
    const [featuredPerks, setFeaturedPerks] = useState([]);
    const [clubFilterName, setClubFilterName] = useState('');
    
    const [filters, setFilters] = useState({
        query: '',
        clubId: searchParams.get('clubId') || '',
        category: '',
        firearmSection: '',
        condition: '',
        minPrice: '',
        maxPrice: '',
        sortBy: 'Newest'
    });
    
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 20,
        totalPages: 1,
        totalCount: 0
    });

    const hasActiveFilters = Boolean(
        filters.query ||
        filters.clubId ||
        filters.category ||
        filters.firearmSection ||
        filters.condition ||
        filters.minPrice ||
        filters.maxPrice
    );

    const categoryOptions = [
        { value: '', label: 'All Categories' },
        { value: 'Shotgun', label: 'Shotguns' },
        { value: 'Rifle', label: 'Rifles' },
        { value: 'Ammunition', label: 'Ammunition' },
        { value: 'Optics', label: 'Optics & Scopes' },
        { value: 'Accessories', label: 'Accessories' },
        { value: 'Clothing', label: 'Clothing' },
        { value: 'Cases', label: 'Cases & Storage' },
        { value: 'Cleaning', label: 'Cleaning & Maintenance' },
        { value: 'Targets', label: 'Targets & Traps' }
    ];

    const conditionOptions = [
        { value: '', label: 'Any Condition' },
        { value: 'New', label: 'New' },
        { value: 'Excellent', label: 'Excellent' },
        { value: 'Good', label: 'Good' },
        { value: 'Fair', label: 'Fair' },
        { value: 'Poor', label: 'Poor' }
    ];

    const firearmSectionOptions = [
        { value: '', label: 'All Items' },
        { value: 'None', label: 'Non-Firearm Items' },
        { value: 'Section1', label: 'Section 1 (FAC)' },
        { value: 'Section2', label: 'Section 2 (SGC)' },
        { value: 'Airgun', label: 'Air Guns' }
    ];

    const fetchListings = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            params.append('page', pagination.page);
            params.append('pageSize', pagination.pageSize);
            if (filters.query) params.append('query', filters.query);
            if (filters.clubId) params.append('clubId', filters.clubId);
            if (filters.category) params.append('category', filters.category);
            if (filters.firearmSection) params.append('firearmSection', filters.firearmSection);
            if (filters.condition) params.append('condition', filters.condition);
            if (filters.minPrice) params.append('minPrice', filters.minPrice);
            if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
            params.append('sortBy', filters.sortBy);

            const data = await marketplaceService.getListings(params);
            setListings(data.listings || []);
            setError(null);
            setPagination(prev => ({
                ...prev,
                totalPages: data.totalPages || 1,
                totalCount: data.totalCount || 0
            }));
        } catch (err) {
            console.error('Error fetching listings:', err);
            setError('Failed to load listings');
        } finally {
            setIsLoading(false);
        }
    }, [filters, pagination.page, pagination.pageSize]);

    const fetchCategories = useCallback(async () => {
        try {
            const data = await marketplaceService.getCategories();
            setCategories(data || []);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    }, []);

    const fetchFeaturedPerks = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.append('take', '6');
            params.append('audience', 'Public');
            const data = await clubsService.getLatestOffers(params);
            setFeaturedPerks(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching featured perks:', err);
            setFeaturedPerks([]);
        }
    }, []);

    useEffect(() => {
        fetchListings();
        fetchCategories();
        fetchFeaturedPerks();
    }, [fetchListings, fetchCategories, fetchFeaturedPerks]);

    useEffect(() => {
        const queryClubId = searchParams.get('clubId') || '';
        setFilters((current) => ({
            ...current,
            clubId: queryClubId
        }));
        setPagination((current) => ({
            ...current,
            page: 1
        }));
    }, [searchParams]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchListings();
    };

    useEffect(() => {
        if (!filters.clubId || listings.length === 0) {
            setClubFilterName('');
            return;
        }

        const matchingClubListing = listings.find((listing) => String(listing.clubId) === String(filters.clubId));
        setClubFilterName(matchingClubListing?.sellerName || '');
    }, [filters.clubId, listings]);

    const clearAllFilters = () => {
        setSearchParams({});
        setFilters({
            query: '',
            clubId: '',
            category: '',
            firearmSection: '',
            condition: '',
            minPrice: '',
            maxPrice: '',
            sortBy: 'Newest'
        });
        setPagination((current) => ({ ...current, page: 1 }));
    };

    const handleFavourite = async (e, listingId) => {
        e.stopPropagation();
        try {
            const data = await marketplaceService.toggleFavourite(listingId);
            setListings(prev => prev.map(l => 
                l.listingId === listingId 
                    ? { ...l, isFavourited: data.isFavourited, favouriteCount: l.favouriteCount + (data.isFavourited ? 1 : -1) }
                    : l
            ));
        } catch (err) {
            console.error('Error toggling favourite:', err);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP'
        }).format(price);
    };

    const getFirearmBadge = (section) => {
        switch (section) {
            case 'Section1':
                return <span className="mp-badge mp-badge-fac">FAC Required</span>;
            case 'Section2':
                return <span className="mp-badge mp-badge-sgc">SGC Required</span>;
            default:
                return null;
        }
    };

    const getConditionClass = (condition) => {
        const classes = {
            'New': 'mp-condition-new',
            'Excellent': 'mp-condition-excellent',
            'Good': 'mp-condition-good',
            'Fair': 'mp-condition-fair',
            'Poor': 'mp-condition-poor'
        };
        return classes[condition] || '';
    };

    return (
        <div className="marketplace-page">
            {/* Legal Disclaimer Banner */}
            {showDisclaimer && (
                <div className="mp-disclaimer-banner">
                    <AlertTriangle size={20} />
                    <div className="mp-disclaimer-content">
                        <strong>UK Firearms Legislation Notice</strong>
                        <p>All firearm transactions must comply with UK law. Shotguns require SGC, rifles require FAC. Firearms and ammunition cannot be shipped - collection only with certificate verification.</p>
                    </div>
                    <button onClick={() => setShowDisclaimer(false)}>Dismiss</button>
                </div>
            )}

            {/* Header */}
            <div className="mp-header">
                <div className="mp-header-content">
                    <div className="mp-header-icon">
                        <ShoppingBag size={28} />
                    </div>
                    <div>
                        <h1>Marketplace</h1>
                        <p>Buy and sell shooting equipment with trusted members and venue sellers.</p>
                    </div>
                </div>
                <div className="mp-header-actions">
                    <button 
                        className="mp-btn-primary"
                        onClick={() => navigate('/dashboard/marketplace/create')}
                    >
                        <Plus size={18} />
                        List Item
                    </button>
                    <button 
                        className="mp-btn-secondary"
                        onClick={() => navigate('/dashboard/marketplace/my-listings')}
                    >
                        My Listings
                    </button>
                </div>
            </div>

            {/* Search and Filters */}
            {filters.clubId && (
                <div className="mp-disclaimer-banner" style={{ marginBottom: '16px' }}>
                    <ShoppingBag size={20} />
                    <div className="mp-disclaimer-content">
                        <strong>Venue storefront</strong>
                        <p>Showing listings from {clubFilterName || 'this venue'}.</p>
                    </div>
                    <button onClick={clearAllFilters}>Clear</button>
                </div>
            )}
            <div className="mp-filters-section">
                <form className="mp-search-form" onSubmit={handleSearch}>
                    <div className="mp-search-input">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search equipment..."
                            value={filters.query}
                            onChange={(e) => handleFilterChange('query', e.target.value)}
                        />
                    </div>
                    <button type="submit" className="mp-search-btn">Search</button>
                </form>

                <button 
                    className="mp-filter-toggle"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <Filter size={16} />
                    Filters
                    <ChevronDown size={16} className={showFilters ? 'rotated' : ''} />
                </button>

                {showFilters && (
                    <div className="mp-filters-grid">
                        <div className="mp-filter-group">
                            <label>Category</label>
                            <select 
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                            >
                                {categoryOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="mp-filter-group">
                            <label>Firearm Type</label>
                            <select 
                                value={filters.firearmSection}
                                onChange={(e) => handleFilterChange('firearmSection', e.target.value)}
                            >
                                {firearmSectionOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="mp-filter-group">
                            <label>Condition</label>
                            <select 
                                value={filters.condition}
                                onChange={(e) => handleFilterChange('condition', e.target.value)}
                            >
                                {conditionOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="mp-filter-group">
                            <label>Price Range</label>
                            <div className="mp-price-range">
                                <input 
                                    type="number" 
                                    placeholder="Min"
                                    value={filters.minPrice}
                                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                                />
                                <span>to</span>
                                <input 
                                    type="number" 
                                    placeholder="Max"
                                    value={filters.maxPrice}
                                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="mp-filter-group">
                            <label>Sort By</label>
                            <select 
                                value={filters.sortBy}
                                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                            >
                                <option value="Newest">Newest First</option>
                                <option value="PriceLow">Price: Low to High</option>
                                <option value="PriceHigh">Price: High to Low</option>
                                <option value="MostViewed">Most Viewed</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Quick Category Pills */}
            {categories.length > 0 && (
                <div className="mp-category-pills">
                    <button 
                        className={`mp-pill ${!filters.category ? 'active' : ''}`}
                        onClick={() => handleFilterChange('category', '')}
                    >
                        All ({pagination.totalCount})
                    </button>
                    {categories.slice(0, 6).map(cat => (
                        <button
                            key={cat.category}
                            className={`mp-pill ${filters.category === cat.category ? 'active' : ''}`}
                            onClick={() => handleFilterChange('category', cat.category)}
                        >
                            {cat.category} ({cat.count})
                        </button>
                    ))}
                </div>
            )}

            {featuredPerks.length > 0 && (
                <section className="mp-perks-panel">
                    <div className="mp-perks-header">
                        <h3>
                            <Gift size={18} />
                            Connect Perks from active venues
                        </h3>
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard/connect-perks', { state: { fromDashboard: true, from: '/dashboard/marketplace' } })}
                        >
                            Browse all perks
                        </button>
                    </div>
                    <div className="mp-perks-grid">
                        {featuredPerks.slice(0, 4).map((offer) => (
                            <button
                                type="button"
                                key={offer.clubOfferId}
                                className="mp-perk-card"
                                onClick={() => navigate(getClubPublicPath(offer))}
                            >
                                <strong>{offer.title}</strong>
                                <span>{offer.clubName}</span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* Listings Grid */}
            {isLoading ? (
                <StateCard
                    icon={ShoppingBag}
                    title="Loading marketplace listings"
                    description="Bringing in the latest listings and category matches."
                    loading
                />
            ) : error ? (
                <StateCard
                    icon={AlertTriangle}
                    title="Marketplace listings couldn't be loaded"
                    description={error}
                    tone="error"
                />
            ) : listings.length === 0 ? (
                <StateCard
                    icon={Package}
                    title={hasActiveFilters ? 'No listings match these filters' : 'No listings found'}
                    description={hasActiveFilters ? 'Try widening your filters or search terms, or list the item you want to sell first.' : 'Be the first to list something for the community.'}
                    actionLabel={hasActiveFilters ? 'Clear Filters' : 'Create Listing'}
                    actionOnClick={hasActiveFilters ? clearAllFilters : () => navigate('/dashboard/marketplace/create')}
                />
            ) : (
                <>
                    <div className="mp-results-count">
                        Showing {listings.length} of {pagination.totalCount} items
                    </div>

                    <div className="mp-listings-grid">
                        {listings.map(listing => (
                            <div 
                                key={listing.listingId}
                                className="mp-listing-card"
                                onClick={() => navigate(`/dashboard/marketplace/${listing.listingId}`)}
                            >
                                <div className="mp-listing-image">
                                    {listing.firstImageUrl ? (
                                        <img src={listing.firstImageUrl} alt={listing.title} />
                                    ) : (
                                        <div className="mp-listing-placeholder">
                                            <Package size={40} />
                                        </div>
                                    )}
                                    
                                    {/* Badges */}
                                    <div className="mp-listing-badges">
                                        {getFirearmBadge(listing.firearmSection)}
                                        {listing.collectionOnly && (
                                            <span className="mp-badge mp-badge-collection">
                                                <MapPin size={10} /> Collection Only
                                            </span>
                                        )}
                                    </div>

                                    {/* Favourite Button */}
                                    <button 
                                        className={`mp-favourite-btn ${listing.isFavourited ? 'active' : ''}`}
                                        onClick={(e) => handleFavourite(e, listing.listingId)}
                                    >
                                        <Heart 
                                            size={18} 
                                            fill={listing.isFavourited ? 'currentColor' : 'none'} 
                                        />
                                    </button>
                                </div>

                                <div className="mp-listing-content">
                                    <div className="mp-listing-category">
                                        <Tag size={12} />
                                        {listing.category}
                                    </div>
                                    
                                    <h3 className="mp-listing-title">{listing.title}</h3>
                                    
                                    {(listing.make || listing.model) && (
                                        <p className="mp-listing-subtitle">
                                            {[listing.make, listing.model].filter(Boolean).join(' ')}
                                        </p>
                                    )}

                                    <div className="mp-listing-price">
                                        {formatPrice(listing.price)}
                                    </div>

                                    <div className="mp-listing-meta">
                                        {listing.condition && (
                                            <span className={`mp-condition ${getConditionClass(listing.condition)}`}>
                                                {listing.condition}
                                            </span>
                                        )}
                                        {listing.location && (
                                            <span className="mp-location">
                                                <MapPin size={12} /> {listing.location}
                                            </span>
                                        )}
                                    </div>

                                    <div className="mp-listing-footer">
                                        {listing.sellerType === 'Venue' && listing.clubId ? (
                                            <button
                                                type="button"
                                                className="mp-seller"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    navigate(`/dashboard/marketplace/venue/${listing.clubId}`);
                                                }}
                                            >
                                                {`Venue: ${listing.sellerName}`}
                                            </button>
                                        ) : (
                                            <span className="mp-seller">
                                                {listing.sellerName}
                                            </span>
                                        )}
                                        <div className="mp-stats">
                                            <span><Eye size={12} /> {listing.viewCount}</span>
                                            <span><Heart size={12} /> {listing.favouriteCount}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="mp-pagination">
                            <button
                                disabled={pagination.page === 1}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            >
                                Previous
                            </button>
                            <span>Page {pagination.page} of {pagination.totalPages}</span>
                            <button
                                disabled={pagination.page === pagination.totalPages}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Legal Info Footer */}
            <div className="mp-legal-footer">
                <Shield size={20} />
                <div>
                    <h4>Safe Trading Guidelines</h4>
                    <ul>
                        <li>Always verify certificates in person before any firearm transfer</li>
                        <li>Meet in a safe, public location or at a registered firearms dealer</li>
                        <li>Report suspicious listings or users immediately</li>
                        <li>Never ship firearms or ammunition - collection only</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default Marketplace;
